import { apiGet, apiPatch } from './apiClient';
import { useApiResource } from './useApiResource';

/** Mirrors the backend PlanTier — the one tier vocabulary, not a second one. */
export type PlanTier = 'free' | 'plus' | 'guardian';
export const ALL_TIERS: PlanTier[] = ['free', 'plus', 'guardian'];

export interface ResolvedFeature {
  id: string;
  category: string;
  nameEn: string;
  nameEs: string;
  descriptionEn: string;
  descriptionEs: string;
  tiers: PlanTier[];
  dataCategories: string[];
  safetyCritical?: boolean;
  isOverridden: boolean;
}

/**
 * Display order for the category bands. A category the backend adds that is not
 * listed here still renders — it sorts to the end rather than disappearing,
 * because a new product silently missing from the configurator is the exact
 * failure this catalog exists to prevent.
 */
const CATEGORY_ORDER = ['device', 'ai', 'safety', 'data', 'care'];

export function groupByCategory(features: ResolvedFeature[]): Array<[string, ResolvedFeature[]]> {
  const byCategory = new Map<string, ResolvedFeature[]>();
  for (const f of features) {
    const list = byCategory.get(f.category) ?? [];
    list.push(f);
    byCategory.set(f.category, list);
  }
  return [...byCategory.entries()].sort(([a], [b]) => {
    const ia = CATEGORY_ORDER.indexOf(a);
    const ib = CATEGORY_ORDER.indexOf(b);
    return (ia < 0 ? 999 : ia) - (ib < 0 ? 999 : ib);
  });
}

/**
 * Tiers that would render an empty paywall.
 *
 * Only `free` can legitimately show up here — nobody is sold it, so the
 * backend allows it to resolve to zero features. `plus`/`guardian` are
 * different: the backend now REFUSES any admin edit that would leave a paid
 * tier showing nothing (including one where every remaining feature is only
 * inherited from the tier below — see FeatureCatalogService's shown-list
 * guard), on both the admin API and a value written straight to Firestore.
 * So from a healthy backend this array should never contain 'plus' or
 * 'guardian' — seeing one here is a signal the catalog failed to load
 * cleanly (check useFeatureCatalog's `error`/`loading` before trusting this),
 * not a permitted state the admin merely needs to notice.
 */
export function tiersWithNoFeatures(features: ResolvedFeature[]): PlanTier[] {
  return ALL_TIERS.filter(tier => !features.some(f => f.tiers.includes(tier)));
}

export function useFeatureCatalog() {
  return useApiResource(() => apiGet<{ items: ResolvedFeature[] }>('/admin/features').then(r => r.items ?? []), []);
}

export function setFeatureTiers(id: string, tiers: PlanTier[]) {
  return apiPatch<ResolvedFeature>(`/admin/features/${encodeURIComponent(id)}`, { tiers });
}

export function setFeatureCopy(
  id: string,
  copy: Partial<Pick<ResolvedFeature, 'nameEn' | 'nameEs' | 'descriptionEn' | 'descriptionEs'>>,
) {
  return apiPatch<ResolvedFeature>(`/admin/features/${encodeURIComponent(id)}`, { copy });
}
